import React, { useEffect, useState } from 'react';
import { Loader2, Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';

const emptyCategory = {
  name: '',
  parent_id: '',
  description: '',
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
  visibility: 'public',
};

const emptyBrand = {
  name: '',
  description: '',
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
  visibility: 'public',
};

const htmlToPlainText = (value) => {
  if (!value) return '';
  const documentNode = new DOMParser().parseFromString(value, 'text/html');
  documentNode.querySelectorAll('script, style').forEach((node) => node.remove());
  return (documentNode.body.textContent || '').replace(/\s+/g, ' ').trim();
};

const seoDescriptionFromHtml = (value) => htmlToPlainText(value).slice(0, 160);

export default function CatalogManager({ open = false, onOpenChange, onChanged, defaultTab = 'categories', standalone = false, singleTab = false }) {
  const [tab, setTab] = useState(defaultTab);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [category, setCategory] = useState(emptyCategory);
  const [brand, setBrand] = useState(emptyBrand);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingBrandId, setEditingBrandId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadCatalog = async () => {
    setLoading(true);
    try {
      const [categoryResponse, brandResponse] = await Promise.all([
        api.get('/catalog/categories'),
        api.get('/catalog/brands'),
      ]);
      setCategories(categoryResponse.data || []);
      setBrands(brandResponse.data || []);
    } catch (error) {
      toast.error('Could not load categories and brands');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open || standalone) {
      setTab(defaultTab);
      setCategory(emptyCategory);
      setBrand(emptyBrand);
      setEditingCategoryId(null);
      setEditingBrandId(null);
      loadCatalog();
    }
  }, [open, standalone, defaultTab]);

  const saveCategory = async (event) => {
    event.preventDefault();
    if (!category.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...category,
        parent_id: category.parent_id || null,
        meta_title: category.meta_title || category.name,
      };
      if (editingCategoryId) {
        await api.put(`/catalog/categories/${editingCategoryId}`, payload);
      } else {
        await api.post('/catalog/categories', payload);
      }
      toast.success(editingCategoryId ? 'Category updated' : 'Category created');
      cancelCategoryEdit();
      await loadCatalog();
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Could not ${editingCategoryId ? 'update' : 'create'} category`);
    } finally {
      setSaving(false);
    }
  };

  const saveBrand = async (event) => {
    event.preventDefault();
    if (!brand.name.trim()) return;
    setSaving(true);
    try {
      if (editingBrandId) {
        await api.put(`/catalog/brands/${editingBrandId}`, brand);
      } else {
        await api.post('/catalog/brands', brand);
      }
      toast.success(editingBrandId ? 'Brand updated' : 'Brand created');
      cancelBrandEdit();
      await loadCatalog();
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Could not ${editingBrandId ? 'update' : 'create'} brand`);
    } finally {
      setSaving(false);
    }
  };

  const cancelCategoryEdit = () => {
    setEditingCategoryId(null);
    setCategory(emptyCategory);
  };

  const editCategory = (item) => {
    setEditingCategoryId(item.id);
    setCategory({
      name: item.name || '',
      parent_id: item.parent_id || '',
      description: item.description || '',
      meta_title: item.meta_title || item.name || '',
      meta_description: item.meta_description || '',
      meta_keywords: item.meta_keywords || '',
      visibility: item.visibility || 'public',
    });
  };

  const deleteCategory = async (item) => {
    if (!window.confirm(`Delete category “${item.name}”? Categories used by products or subcategories cannot be deleted.`)) return;
    try {
      await api.delete(`/catalog/categories/${item.id}`);
      if (editingCategoryId === item.id) cancelCategoryEdit();
      toast.success('Category deleted');
      await loadCatalog();
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not delete category');
    }
  };

  const cancelBrandEdit = () => {
    setEditingBrandId(null);
    setBrand(emptyBrand);
  };

  const editBrand = (item) => {
    setEditingBrandId(item.id);
    setBrand({
      name: item.name || '',
      description: item.description || '',
      meta_title: item.meta_title || item.name || '',
      meta_description: item.meta_description || '',
      meta_keywords: item.meta_keywords || '',
      visibility: item.visibility || 'public',
    });
  };

  const deleteBrand = async (item) => {
    if (!window.confirm(`Delete brand “${item.name}”? Brands used by products cannot be deleted.`)) return;
    try {
      await api.delete(`/catalog/brands/${item.id}`);
      if (editingBrandId === item.id) cancelBrandEdit();
      toast.success('Brand deleted');
      await loadCatalog();
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not delete brand');
    }
  };

  const updateCategoryName = (name) => {
    setCategory((current) => ({
      ...current,
      name,
      meta_title: !current.meta_title || current.meta_title === current.name ? name : current.meta_title,
    }));
  };

  const updateBrandName = (name) => {
    setBrand((current) => ({
      ...current,
      name,
      meta_title: !current.meta_title || current.meta_title === current.name ? name : current.meta_title,
    }));
  };

  const mainCategories = categories.filter((item) => !item.parent_id && item.id !== editingCategoryId);
  const selectedParent = mainCategories.find((item) => item.id === category.parent_id);
  const categoryParentIsPrivate = selectedParent?.visibility === 'private';

  const content = (
    <>
      {standalone ? (
        <div className="mb-6">
          <p className="text-sm font-medium uppercase tracking-[0.18em] text-slate-400">Storefront Catalogue</p>
          <h2 className="mt-1 text-2xl font-bold text-slate-900">
            {defaultTab === 'brands' ? 'Brands' : 'Categories'}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Manage the catalogue records used by products, receiving, filters, and storefront SEO.
          </p>
        </div>
      ) : (
        <DialogHeader>
          <DialogTitle>Categories &amp; Brands</DialogTitle>
          <DialogDescription>
            These records are saved in the ERP database and are available immediately when creating and receiving products.
          </DialogDescription>
        </DialogHeader>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        {!singleTab && (
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="categories">Categories</TabsTrigger>
            <TabsTrigger value="brands">Brands</TabsTrigger>
          </TabsList>
        )}

          <TabsContent value="categories" className="space-y-5">
            <form onSubmit={saveCategory} className="space-y-4 rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <p className="font-medium">{editingCategoryId ? 'Edit category' : 'Create category'}</p>
                {editingCategoryId && (
                  <Button type="button" variant="ghost" size="sm" onClick={cancelCategoryEdit} className="gap-1">
                    <X className="h-4 w-4" /> Cancel editing
                  </Button>
                )}
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label>Category name *</Label>
                  <Input
                    value={category.name}
                    onChange={(event) => updateCategoryName(event.target.value)}
                    placeholder="e.g. T-Shirts"
                    maxLength={120}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Parent category</Label>
                  <Select value={category.parent_id || 'main'} onValueChange={(value) => { const parentId = value === 'main' ? '' : value; const parent = mainCategories.find((item) => item.id === parentId); setCategory({ ...category, parent_id: parentId, visibility: parent?.visibility === 'private' ? 'private' : category.visibility }); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="main">None — main category</SelectItem>
                      {mainCategories.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Storefront visibility</Label>
                  <Select value={category.visibility || 'public'} onValueChange={(value) => setCategory({ ...category, visibility: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public" disabled={categoryParentIsPrivate}>Public — show on storefront</SelectItem>
                      <SelectItem value="private">Private — ERP only</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Private categories are excluded from filters, search results, product breadcrumbs, and public APIs. A child of a private category is automatically private.</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>HTML description</Label>
                <Textarea
                  value={category.description}
                  onChange={(event) => setCategory({ ...category, description: event.target.value })}
                  rows={12}
                  className="font-mono text-xs leading-5"
                  placeholder={'<h2>Premium Regular Fit T-Shirts in Sri Lanka</h2>\n<p>Explore premium T-shirts by <strong>URBNSW</strong>...</p>\n<h3>Premium Materials</h3>\n<p>Every T-shirt uses carefully selected cotton.</p>'}
                />
                <p className="text-xs text-slate-500">
                  HTML is supported. Recommended tags: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;ol&gt; and &lt;li&gt;.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>SEO title <span className="text-slate-400">({category.meta_title.length}/60)</span></Label>
                  <Input value={category.meta_title} onChange={(event) => setCategory({ ...category, meta_title: event.target.value })} maxLength={60} />
                </div>
                <div className="space-y-2">
                  <Label>SEO keywords</Label>
                  <Input value={category.meta_keywords} onChange={(event) => setCategory({ ...category, meta_keywords: event.target.value })} placeholder="comma, separated, keywords" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label>SEO description <span className="text-slate-400">({category.meta_description.length}/160)</span></Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCategory({ ...category, meta_description: seoDescriptionFromHtml(category.description) })}
                    disabled={!category.description}
                  >
                    Generate from HTML
                  </Button>
                </div>
                <Textarea value={category.meta_description} onChange={(event) => setCategory({ ...category, meta_description: event.target.value })} maxLength={160} rows={2} />
                <p className="text-xs text-slate-500">Plain text only. Search engines do not use HTML inside a meta description.</p>
              </div>
              <Button type="submit" disabled={saving} className="gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingCategoryId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {editingCategoryId ? 'Save category changes' : 'Create category'}
              </Button>
            </form>

            <div className="rounded-lg border p-4">
              <p className="mb-3 font-medium">Current categories</p>
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                <div className="space-y-2">
                  {categories.map((item) => (
                    <div key={item.id} className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm ${editingCategoryId === item.id ? 'border-indigo-300 bg-indigo-50' : 'border-transparent bg-slate-50'}`}>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate font-medium">{item.parent_name && <span className="text-slate-400">{item.parent_name} / </span>}{item.name}</p>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${item.visibility === 'private' ? 'bg-slate-200 text-slate-600' : 'bg-emerald-100 text-emerald-700'}`}>
                            {item.visibility === 'private' ? 'Private' : 'Public'}
                          </span>
                        </div>
                        {item.description && <p className="truncate text-xs text-slate-500">{htmlToPlainText(item.description)}</p>}
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => editCategory(item)} aria-label={`Edit ${item.name}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => deleteCategory(item)} aria-label={`Delete ${item.name}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {categories.length === 0 && <p className="text-sm text-slate-500">No categories yet.</p>}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="brands" className="space-y-5">
            <form onSubmit={saveBrand} className="space-y-4 rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <p className="font-medium">{editingBrandId ? 'Edit brand' : 'Create brand'}</p>
                {editingBrandId && (
                  <Button type="button" variant="ghost" size="sm" onClick={cancelBrandEdit} className="gap-1">
                    <X className="h-4 w-4" /> Cancel editing
                  </Button>
                )}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Brand name *</Label>
                  <Input value={brand.name} onChange={(event) => updateBrandName(event.target.value)} placeholder="e.g. URBNSW" required />
                </div>
                <div className="space-y-2">
                  <Label>Storefront visibility</Label>
                  <Select value={brand.visibility || 'public'} onValueChange={(value) => setBrand({ ...brand, visibility: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Public — show on storefront</SelectItem>
                      <SelectItem value="private">Private — ERP only</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Products assigned to a private native brand are excluded from the public storefront.</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>HTML description</Label>
                <Textarea
                  value={brand.description}
                  onChange={(event) => setBrand({ ...brand, description: event.target.value })}
                  rows={12}
                  className="font-mono text-xs leading-5"
                  placeholder={'<h2>Premium Streetwear by URBNSW</h2>\n<p>Discover original designs made with <strong>premium materials</strong>.</p>'}
                />
                <p className="text-xs text-slate-500">
                  HTML is supported. Recommended tags: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;ol&gt; and &lt;li&gt;.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>SEO title <span className="text-slate-400">({brand.meta_title.length}/60)</span></Label>
                  <Input value={brand.meta_title} onChange={(event) => setBrand({ ...brand, meta_title: event.target.value })} maxLength={60} />
                </div>
                <div className="space-y-2">
                  <Label>SEO keywords</Label>
                  <Input value={brand.meta_keywords} onChange={(event) => setBrand({ ...brand, meta_keywords: event.target.value })} placeholder="comma, separated, keywords" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label>SEO description <span className="text-slate-400">({brand.meta_description.length}/160)</span></Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setBrand({ ...brand, meta_description: seoDescriptionFromHtml(brand.description) })}
                    disabled={!brand.description}
                  >
                    Generate from HTML
                  </Button>
                </div>
                <Textarea value={brand.meta_description} onChange={(event) => setBrand({ ...brand, meta_description: event.target.value })} maxLength={160} rows={2} />
                <p className="text-xs text-slate-500">Plain text only. Search engines do not use HTML inside a meta description.</p>
              </div>
              <Button type="submit" disabled={saving} className="gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingBrandId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {editingBrandId ? 'Save brand changes' : 'Create brand'}
              </Button>
            </form>
            <div className="rounded-lg border p-4">
              <p className="mb-3 font-medium">Current brands</p>
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                <div className="space-y-2">
                  {brands.map((item) => (
                    <div key={item.id} className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm ${editingBrandId === item.id ? 'border-indigo-300 bg-indigo-50' : 'border-transparent bg-slate-50'}`}>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate font-medium">{item.name}</p>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${item.visibility === 'private' ? 'bg-slate-200 text-slate-600' : 'bg-emerald-100 text-emerald-700'}`}>
                            {item.visibility === 'private' ? 'Private' : 'Public'}
                          </span>
                        </div>
                        {item.description && <p className="truncate text-xs text-slate-500">{htmlToPlainText(item.description)}</p>}
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => editBrand(item)} aria-label={`Edit ${item.name}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => deleteBrand(item)} aria-label={`Delete ${item.name}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {brands.length === 0 && <p className="text-sm text-slate-500">No brands yet.</p>}
                </div>
              )}
            </div>
          </TabsContent>
      </Tabs>
    </>
  );

  if (standalone) {
    return (
      <div className="mx-auto w-full max-w-5xl rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        {content}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        {content}
      </DialogContent>
    </Dialog>
  );
}
